import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Box, Select } from 'stylewhere/components'
import { CalendarPicker } from './CalendarPicker'
import { components } from 'react-select'

interface Option {
  label: string
  value: string
}

interface SelectDateProps {
  label?: string
  value?: string | { start: string; end: string }
  onChange: (val: string | { start: string; end: string } | undefined) => void
  options: Option[]
  placeholder?: string
  disabled?: boolean
}

const CUSTOM_VALUE = 'specific'

function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function convertRangeToDateLabel(start: string, end: string): string {
  const dStart = new Date(start).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  const dEnd = new Date(end).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  return `${dStart} - ${dEnd}`
}

const makeCustomMenuList = (
  range: { start: Date | null; end: Date | null },
  onCalendarChange: (dates: [Date | null, Date | null]) => void
): typeof components.MenuList =>
  function CustomMenuList(props) {
    const selectOnChange = props.selectProps.onChange
    return (
      <components.MenuList {...props}>
        {props.children}
        <Box mt={2} p={2} style={{ display: 'flex', justifyContent: 'center' }}>
          <CalendarPicker
            startDate={range.start}
            endDate={range.end}
            onChange={(dates) => {
              onCalendarChange(dates)
              const [start, end] = dates
              if (start && end) {
                const localStart = toLocalDateString(start)
                const localEnd = toLocalDateString(end)
                const customOption = {
                  label: convertRangeToDateLabel(localStart, localEnd),
                  value: CUSTOM_VALUE,
                  dateRange: { start: localStart, end: localEnd }
                }
                selectOnChange(customOption as any, { action: 'select-option', option: customOption as any })
              }
            }}
          />
        </Box>
      </components.MenuList>
    )
  }

export const SelectDate: React.FC<SelectDateProps> = ({ value, onChange, options, placeholder, label, disabled }) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null)
  const [range, setRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null })

  useEffect(() => {
    if (typeof value === 'string') {
      const match = options.find((o) => o.value === value)
      setSelectedOption(match || null)
      setRange({ start: null, end: null })
    } else if (value && typeof value === 'object' && value.start && value.end) {
      const displayLabel = convertRangeToDateLabel(value.start, value.end)
      setSelectedOption({ label: displayLabel, value: CUSTOM_VALUE })
      setRange({ start: new Date(value.start), end: new Date(value.end) })
    } else {
      setSelectedOption(null)
      setRange({ start: null, end: null })
    }
  }, [value, options])

  const handleSelect = (selected: any) => {
    if (!selected) {
      onChange(undefined)
      return
    }
    if (selected.value === CUSTOM_VALUE && selected.dateRange) {
      onChange({
        start: selected.dateRange.start,
        end: selected.dateRange.end
      })
    } else {
      onChange(selected.value)
    }
  }

  const handleCalendarChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates
      setRange({ start, end })
      if (start && end) {
        onChange({
          start: toLocalDateString(start),
          end: toLocalDateString(end)
        })
      }
    },
    [onChange]
  )

  const customMenuList = useMemo(() => makeCustomMenuList(range, handleCalendarChange), [range, handleCalendarChange])

  const filteredOptions = options.filter((opt) => opt.value !== CUSTOM_VALUE)

  return (
    <Select
      label={label}
      config={{ value: 'value', label: 'label' }}
      options={filteredOptions}
      value={selectedOption}
      onSelect={handleSelect}
      placeholder={placeholder || 'Seleziona data'}
      customComponents={{ MenuList: customMenuList }}
      isClearable={true}
      disabled={disabled}
    />
  )
}
